"use client";

import {
	Badge,
	Button,
	DefaultImageProps,
	DefaultLinkProps,
	Heading,
	type HeadingProps,
	Image,
	ImageProps,
	LinkProps,
	Modal,
	PromotionalBadge,
	PromotionalBadgeProps,
	RichText,
	Tag,
	TagProps
} from "@pamperedchef/design-system";
import { className as classNameHelper } from "@/app/utilities/className";
import { objectHasValues } from "@/app/utilities/objectHasValues";
import { type MotionProps, motion } from "framer-motion";

import * as styles from "./FeaturedOfferCard.module.scss";

export interface FeaturedOfferCardProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> extends React.HTMLAttributes<HTMLDivElement> {
	audienceTag: TagProps;
	cardTitle: HeadingProps;
	cardDescription: string;
	promotionalBadge?: PromotionalBadgeProps;
	badgeText?: string;
	badgeCtaLabel?: string;
	badgeCtaModal?: {
		title: string;
		titleTag: HeadingProps["tag"];
		description: string;
	};
	ctas?:
		| [LinkProps<LinkComponentProps>]
		| [LinkProps<LinkComponentProps>, LinkProps<LinkComponentProps>];
	image?: ImageProps<ImageComponentProps>;
	motionProps?: MotionProps;
}

/**
 * Featured Offert Card component
 */
export function FeaturedOfferCard<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
>({
	audienceTag,
	cardTitle,
	cardDescription,
	promotionalBadge,
	badgeText,
	badgeCtaLabel,
	badgeCtaModal,
	ctas,
	image,
	className,
	motionProps,
	...props
}: FeaturedOfferCardProps<
	ImageComponentProps,
	LinkComponentProps
>): JSX.Element {
	const DivComponent = motionProps ? motion.div : "div";

	return (
		<DivComponent
			{...props}
			{...(motionProps as any)}
			{...classNameHelper(styles["container"], className)}
		>
			<div className={styles["content"]}>
				<div className={styles["content-text"]}>
					<Tag
						{...audienceTag}
						{...classNameHelper(
							styles["audience-tag"],
							audienceTag.className
						)}
					/>
					<Heading
						{...cardTitle}
						{...classNameHelper(
							styles["title"],
							cardTitle.className
						)}
						styledAs={cardTitle.styledAs ?? "h2"}
					/>
					<RichText
						content={cardDescription}
						className={styles["description"]}
					/>
				</div>

				{objectHasValues(promotionalBadge) && (
					<>
						{promotionalBadge?.promoCode && (
							<PromotionalBadge
								{...promotionalBadge}
								className={styles["copy-badge"]}
							/>
						)}
						{promotionalBadge?.text &&
							!promotionalBadge?.promoCode && (
								<Badge
									label={promotionalBadge.text}
									icon={{ name: "Check" }}
									className={styles["promo-badge"]}
								/>
							)}
					</>
				)}

				{badgeText && (
					<div className={styles["badge-container"]}>
						<Badge
							kind="default"
							className={styles["badge"]}
							icon={{ name: "Info" }}
							label={badgeText}
						/>
						{badgeCtaLabel && (
							<Modal
								triggerElement={
									<Button
										kind="tertiary"
										className={
											styles["see-details-trigger"]
										}
									>
										{badgeCtaLabel}
									</Button>
								}
							>
								<Heading
									tag={badgeCtaModal?.titleTag ?? "h1"}
									styledAs="h2"
								>
									{badgeCtaModal?.title}
								</Heading>
								<RichText
									className={styles["modal-text"]}
									content={badgeCtaModal?.description ?? ""}
								/>
							</Modal>
						)}
					</div>
				)}

				{ctas?.length && (
					<div className={styles["cta-container"]}>
						{ctas.slice(0, 2).map((cta, index) => (
							<Button
								key={index}
								kind={index === 0 ? "primary" : "secondary"}
								size="lg"
								onPress={() => {
									const href = (cta as any).href;
									const target = (cta as any).target;
									if (target === "_blank") {
										window.open(href, "_blank");
									} else {
										window.location.href = href;
									}
								}}
							>
								{cta.children}
							</Button>
						))}
					</div>
				)}
			</div>

			{image && (
				<div className={styles["image"]}>
					{/* @ts-expect-error - React 19 generic type limitation with Image component */}
					<Image
						{...image}
						className={styles["image__figure"]}
						aspectRatio={image.aspectRatio ?? "16:9"}
						alt={image.alt || ""}
					/>
				</div>
			)}
		</DivComponent>
	);
}
